import type { NewsArticle } from '../mocks/news';
import { decodeHtmlEntities } from '../utils/decodeHtmlEntities';

export interface CmsNewsListParams {
  locale?: string;
  category?: 'all' | string;
  page?: number;
  pageSize?: number;
}

export interface CmsNewsListResponse {
  items: NewsArticle[];
  total: number;
  page: number;
  pageSize: number;
}

function cmsNewsBase(): string {
  return (import.meta.env.VITE_CMS_NEWS_API_BASE ?? '').replace(/\/$/, '');
}

export function isCmsNewsApiEnabled(): boolean {
  return cmsNewsBase().length > 0;
}

function buildUrl(
  method: 'listing' | 'show' | 'categories',
  params: Record<string, string | number | undefined> = {},
): string {
  const base = cmsNewsBase();
  const q = new URLSearchParams();
  q.set('s', 'news');
  q.set('c', 'json');
  q.set('m', method);
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue;
    q.set(k, String(v));
  }
  return `${base}/index.php?${q.toString()}`;
}

interface CmsJson<T> {
  code: number;
  msg: string;
  data: T;
}

async function parseJson<T>(res: Response): Promise<CmsJson<T>> {
  const text = await res.text();
  try {
    return JSON.parse(text) as CmsJson<T>;
  } catch {
    throw new Error('CMS 返回非 JSON');
  }
}

/** 迅睿等 CMS 常在 JSON 中对标题/摘要等做 htmlspecialchars，展示前统一解码 */
function normalizeNewsArticleFromCms(article: NewsArticle): NewsArticle {
  return {
    ...article,
    title: decodeHtmlEntities(article.title),
    summary: decodeHtmlEntities(article.summary),
    content: decodeHtmlEntities(article.content),
    author: decodeHtmlEntities(article.author),
    authorRole: decodeHtmlEntities(article.authorRole),
    readTime: decodeHtmlEntities(article.readTime),
    categoryLabel: decodeHtmlEntities(article.categoryLabel),
    tags: article.tags.map((t) => decodeHtmlEntities(t)),
  };
}

export async function fetchNewsListFromCms(params: CmsNewsListParams = {}): Promise<CmsNewsListResponse> {
  const category = params.category ?? 'all';
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 50;
  const locale = params.locale ?? 'zh-CN';

  const url = buildUrl('listing', { category, page, pageSize, locale });
  const res = await fetch(url, { method: 'GET', credentials: 'omit' });
  if (!res.ok) {
    throw new Error(`CMS 新闻列表 HTTP ${res.status}`);
  }
  const body = await parseJson<{
    items: NewsArticle[];
    total: number;
    page: number;
    pageSize: number;
  }>(res);
  if (body.code !== 1 || !body.data) {
    throw new Error(body.msg || 'CMS 新闻列表失败');
  }
  return {
    items: body.data.items.map(normalizeNewsArticleFromCms),
    total: body.data.total,
    page: body.data.page,
    pageSize: body.data.pageSize,
  };
}

export async function fetchNewsByIdFromCms(id: string, locale?: string): Promise<NewsArticle | null> {
  const url = buildUrl('show', { id, locale: locale ?? 'zh-CN' });
  const res = await fetch(url, { method: 'GET', credentials: 'omit' });
  if (!res.ok) {
    return null;
  }
  const body = await parseJson<NewsArticle>(res);
  if (body.code !== 1 || !body.data) {
    return null;
  }
  return normalizeNewsArticleFromCms(body.data);
}

export async function fetchNewsCategoriesFromCms(
  locale?: string,
): Promise<Array<{ key: string; label: string; total: number }>> {
  const url = buildUrl('categories', { locale: locale ?? 'zh-CN' });
  const res = await fetch(url, { method: 'GET', credentials: 'omit' });
  if (!res.ok) {
    throw new Error(`CMS 新闻栏目 HTTP ${res.status}`);
  }
  const body = await parseJson<{ items: Array<{ key: string; label: string; total: number }> }>(res);
  if (body.code !== 1 || !body.data?.items) {
    throw new Error(body.msg || 'CMS 新闻栏目失败');
  }
  return body.data.items.map((item) => ({
    ...item,
    label: decodeHtmlEntities(item.label),
  }));
}
